if ( typeof PickupAvailabilityCompact !== 'function' ) {

  // Hatchet Farm ships from Nassau only — the other stores are retail floors.
  // A delivery's timing therefore comes from Nassau, never the browsing store.
  const SHIP_FROM_STORE_HANDLE = 'nassau';

  class PickupAvailabilityCompact extends HTMLElement {

    constructor(){

      super();
      this.classList.add('active');
      this._stockData = null;
      this._sectionHTML = null;

      this.storeSelector = document.querySelector('store-selector[data-main-selector]');
      if ( this.storeSelector ) {
        const STORE_CHANGED_EVENT = () => {
          if (this._stockData) {
            this.renderWithStock();
          } else if (this._sectionHTML) {
            this.renderPreview(this._sectionHTML);
          }
        }
        this.storeSelector.addEventListener('storechanged', STORE_CHANGED_EVENT);
        this.storeSelector.addEventListener('storelocationsearched', STORE_CHANGED_EVENT);
				const observer = new MutationObserver(()=>{
          this.storeSelector.removeEventListener('storechanged', STORE_CHANGED_EVENT);
          this.storeSelector.removeEventListener('storelocationsearched', STORE_CHANGED_EVENT);
					observer.disconnect();
				});
				observer.observe(this.parentElement, {
					attributes: false, childList: true, subtree: false
				});
      }

      if ( this.hasAttribute('data-static') ) {
        this.fetchAvailability(this.dataset.variantId);
      } else {
        PickupAvailabilityCompact.observeForBatch(this);
      }

    }

    disconnectedCallback() {
      PickupAvailabilityCompact.batchObserver?.unobserve(this);

      const variantId = this.dataset.variantId;
      const pendingElements = PickupAvailabilityCompact.pendingBatch.get(variantId);
      if (!pendingElements) return;

      pendingElements.delete(this);
      if (pendingElements.size === 0) {
        PickupAvailabilityCompact.pendingBatch.delete(variantId);
      }
    }

    static stockCache = new Map();
    static pendingBatch = new Map();
    static batchTimer = null;
    static batchObserver = null;
    static DEFAULT_STORE = 'nassau';
    // home handle -> [{handle, dist}] other stores ranked by distance. Built once
    // per home store and cached for the page (store coords don't change mid-session),
    // so we never recompute the ranking on each render.
    static distanceRankCache = new Map();

    static observeForBatch(element) {
      const variantId = element.dataset.variantId;
      if (!variantId) return;

      if (PickupAvailabilityCompact.stockCache.has(variantId)) {
        element.applyStockData(PickupAvailabilityCompact.stockCache.get(variantId));
        return;
      }

      if (!('IntersectionObserver' in window)) {
        PickupAvailabilityCompact.queueBatchFetch(element);
        return;
      }

      if (!PickupAvailabilityCompact.batchObserver) {
        PickupAvailabilityCompact.batchObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            PickupAvailabilityCompact.queueBatchFetch(entry.target);
          });
        }, {rootMargin: '0px 0px 150px 0px'});
      }

      PickupAvailabilityCompact.batchObserver.observe(element);
    }

    static queueBatchFetch(element) {
      const variantId = element.dataset.variantId;
      if (!variantId || !/^\d+$/.test(String(variantId))) return;

      if (PickupAvailabilityCompact.stockCache.has(variantId)) {
        element.applyStockData(PickupAvailabilityCompact.stockCache.get(variantId));
        return;
      }

      const elements = PickupAvailabilityCompact.pendingBatch.get(variantId) || new Set();
      elements.add(element);
      PickupAvailabilityCompact.pendingBatch.set(variantId, elements);

      if (!PickupAvailabilityCompact.batchTimer) {
        PickupAvailabilityCompact.batchTimer = window.setTimeout(() => {
          PickupAvailabilityCompact.flushBatchFetch();
        }, 25);
      }
    }

    static flushBatchFetch() {
      const pending = PickupAvailabilityCompact.pendingBatch;
      PickupAvailabilityCompact.pendingBatch = new Map();
      PickupAvailabilityCompact.batchTimer = null;

      const variantIds = [...pending.keys()];
      if (variantIds.length === 0) return;

      fetch(`/apps/roo/stock-locations?variant_ids=${variantIds.join(',')}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const variants = data?.variants || {};

          variantIds.forEach(variantId => {
            const stockData = variants[variantId] || { locations: {} };
            PickupAvailabilityCompact.stockCache.set(variantId, stockData);
            pending.get(variantId)?.forEach(element => {
              if (element.isConnected) element.applyStockData(stockData);
            });
          });
        })
        .catch(() => {
          variantIds.forEach(variantId => {
            const stockData = { locations: {} };
            PickupAvailabilityCompact.stockCache.set(variantId, stockData);
            pending.get(variantId)?.forEach(element => {
              if (element.isConnected) element.applyStockData(stockData);
            });
          });
        });
    }

    applyStockData(stockData) {
      this._stockData = stockData || { locations: {} };
      this.renderWithStock();
    }

    isFulfillmentPanel() {
      return this.dataset.fulfillmentPanel === 'true';
    }

    fetchAvailability(variantId, forceFetch = false) {

      const pickupAvailabilityData = this.querySelector('[data-js-pickup-availability-data]');

      if ( pickupAvailabilityData && ! forceFetch ) {
        this._sectionHTML = pickupAvailabilityData;
        this.renderPreview(pickupAvailabilityData);
      } else {

        const variantSectionUrl = `${this.dataset.baseUrl.endsWith('/')?this.dataset.baseUrl:`${this.dataset.baseUrl}/`}variants/${variantId}/?section_id=helper-pickup-availability-compact`;

        fetch(variantSectionUrl)
          .then(response => response.text())
          .then(text => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this._sectionHTML = sectionInnerHTML;
            this.renderPreview(sectionInnerHTML);
          })
          .catch(e => {
            console.log(e);
          });

      }

      // Fetch per-location stock data via Shopify App Proxy (HMAC-secured)
      if (!this._stockData || forceFetch) {
        fetch(`/apps/roo/stock-locations?variant_id=${encodeURIComponent(variantId)}`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data) {
              this.applyStockData(data);
            }
          })
          .catch(() => { /* API unavailable — Liquid defaults remain */ });
      }

    }

    // Store hours: [open, close] per day (0=Sun..6=Sat), Eastern time.
    // 30 min after open to 1 hr before close = valid pickup window.
    static STORE_HOURS = {
      "brunswick":       [[10,16],[8,19],[8,19],[8,19],[8,19],[8,19],[8,18]],
      "colonie":         [[10,16],[8,19],[8,19],[8,19],[8,19],[8,19],[8,18]],
      "coxsackie":       [[10,16],[8,19],[8,19],[8,19],[8,19],[8,19],[8,18]],
      "nassau":          [[10,16],[8,19],[8,19],[8,19],[8,19],[8,19],[8,18]],
      "waterford":       [[10,16],[8,19],[8,19],[8,19],[8,19],[8,19],[8,18]],
      "wynantskill":     [[10,16],[8,19],[8,19],[8,19],[8,19],[8,19],[8,18]],
      "niskayuna":       [[10,16],[8,18],[8,18],[8,18],[8,18],[8,18],[8,18]],
      "palatine-bridge": [[10,16],[8,18],[8,18],[8,18],[8,18],[8,18],[8,17]],
      "downtown-troy":   [[0,0],  [8,17],[8,17],[8,17],[8,17],[8,17],[8,13]],
    };

    isWithinPickupWindow(storeHandle) {
      const now = new Date();
      const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const day = et.getDay();
      const currentMin = et.getHours() * 60 + et.getMinutes();
      const hours = PickupAvailabilityCompact.STORE_HOURS[storeHandle];
      if (!hours) return false;
      const [open, close] = hours[day];
      if (open === 0 && close === 0) return false;
      return currentMin >= (open * 60 + 30) && currentMin <= (close * 60 - 60);
    }

    static DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Earliest day an in-stock item can actually be picked up, as a label
    // ("today" / "tomorrow" / weekday). "Today" only holds while we're still
    // before today's pickup cutoff (1 hr before close) on an open day; once that
    // passes — or the store is closed today — it rolls to the next open day.
    getInStockPickupLabel(storeHandle) {
      const hours = PickupAvailabilityCompact.STORE_HOURS[storeHandle];
      if (!hours) return 'today';
      const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const day = et.getDay();
      const currentMin = et.getHours() * 60 + et.getMinutes();
      const isOpenDay = (d) => { const [o, c] = hours[d]; return !(o === 0 && c === 0); };
      // Still pickable today if the store is open today and we're before the cutoff.
      if (isOpenDay(day) && currentMin <= (hours[day][1] * 60 - 60)) return 'today';
      for (let i = 1; i <= 7; i++) {
        const d = (day + i) % 7;
        if (isOpenDay(d)) return i === 1 ? 'tomorrow' : PickupAvailabilityCompact.DAY_NAMES[d];
      }
      return 'today';
    }

    renderWithStock() {
      if (this.isFulfillmentPanel()) {
        this.renderFulfillmentPanel(this._stockData);
        return;
      }

      const selectedStore = localStorage.getItem('selected-store');
      // No store chosen yet: don't leave the card stuck on "Checking local
      // availability" — show overall availability and let the header prompt store choice.
      if (!this.storeSelector || !selectedStore || !this.storeSelector.storesList[selectedStore]) {
        const locs = (this._stockData && this._stockData.locations) || {};
        const inStockSomewhere = Object.values(locs).some(v => v === true);
        const variantAvail = this.dataset.variantAvailable !== 'false';
        if (inStockSomewhere) {
          this.innerHTML = '<span class="alert alert--circle alert--success">In stock — select a store for pickup</span>';
        } else if (variantAvail) {
          this.innerHTML = '<span class="alert alert--circle alert--note">Available to order</span>';
        } else {
          this.innerHTML = '<span class="alert alert--circle alert--note">Currently unavailable</span>';
        }
        return;
      }

      const storeName = this.storeSelector.storesList[selectedStore];
      const safeStoreName = this.escapeHTML(storeName);
      const locations = this._stockData.locations || {};
      const hasStock = locations[selectedStore] === true;
      const hasStockAnywhere = Object.values(locations).some(v => v === true);
      const variantAvailable = this.dataset.variantAvailable !== 'false';

      if (hasStock) {
        this.innerHTML = `<span class="alert alert--circle alert--success">In stock at ${safeStoreName} - ready for pickup ${this.escapeHTML(this.getInStockPickupLabel(selectedStore))}</span>`;
      } else if (hasStockAnywhere) {
        this.innerHTML = `<span class="alert alert--circle alert--note">Ships to ${safeStoreName} - ${this.escapeHTML(this.readyByText(selectedStore))}</span>`;
      } else if (variantAvailable) {
        this.innerHTML = `<span class="alert alert--circle alert--note">Special order - ${this.escapeHTML(this.readyByText(selectedStore))}</span>`;
      } else {
        this.innerHTML = '<span class="alert alert--circle alert--note">Currently unavailable</span>';
      }
    }

    renderFulfillmentPanel(stockData = null) {
      // Effective store = the shopper's selection, or the default store (Nassau)
      // so the panel always shows a concrete "Store at <location>".
      const selectedStore = this.getEffectiveStore();
      const hasStockData = !!(stockData && stockData.locations);
      const locations = hasStockData ? stockData.locations : {};
      const selectedMeta = selectedStore ? this.getStoreMeta(selectedStore) : null;
      const storeLocationLabel = this.getStoreLocationLabel(selectedStore);
      const storeZip = this.getStoreZip(selectedStore);
      const storeHeaderText = storeZip && !storeLocationLabel.includes(storeZip)
        ? `${storeLocationLabel} (${storeZip})`
        : storeLocationLabel;
      const deliveryZip = this.getDeliveryZip(selectedStore) || '12123';
      const inStockHandles = Object.keys(locations).filter(handle => locations[handle] === true);
      const selectedHasStock = !!(selectedStore && locations[selectedStore] === true);
      const variantAvailable = this.dataset.variantAvailable !== 'false';
      const deliveryDate = variantAvailable ? this.getEarliestDeliveryDate(this._deliveryResult) : null;
      const fasterCount = selectedHasStock ? 0 : inStockHandles.filter(handle => handle !== selectedStore).length;
      const method = this._fulfillmentMethod === 'delivery' ? 'delivery' : 'pickup';
      const pickupSelected = method === 'pickup';
      const deliverySelected = method === 'delivery';

      const storeShortName = selectedMeta?.name || this.formatHandle(selectedStore || 'nassau');
      let pickupPrimaryLine = '';
      let pickupSubLine = '';
      if (selectedHasStock) {
        pickupPrimaryLine = `Get it <strong>${this.escapeHTML(this.getInStockPickupLabel(selectedStore))}</strong> at ${this.escapeHTML(storeShortName)}`;
        pickupSubLine = 'In stock at store';
      } else if (fasterCount > 0) {
        pickupPrimaryLine = `Get it nearby <strong>today</strong>`;
        pickupSubLine = `${fasterCount} ${fasterCount === 1 ? 'store' : 'stores'} in stock nearby`;
      } else {
        pickupPrimaryLine = `Get it <strong>${this.escapeHTML(this.getPickupEtaPhrase(selectedStore))}</strong>`;
        pickupSubLine = 'Ships to store for pickup';
      }

      const isNoGround = this.dataset.noGround === 'true' || !!(this._deliveryResult && this._deliveryResult.fedex && this._deliveryResult.fedex.no_ground_shipping);
      const showNoGroundBanner = isNoGround && this.dataset.deliveryTerms !== '1';

      let deliveryPrimaryLine = this.dataset.specialOrder === 'true'
        ? `<strong>${this.escapeHTML(this.dataset.specialOrderMessage || 'Ships in 3–5 days')}</strong>`
        : 'As soon as <strong>tomorrow</strong>';
      let deliverySubLine = this.getDeliverySubLine(this._deliveryResult);
      if (!variantAvailable) {
        deliveryPrimaryLine = 'Unavailable for this variant';
        deliverySubLine = 'Select an available option';
      } else if (this._deliveryResult && this._deliveryResult.restricted_state) {
        const st = (this._deliveryResult.restricted_states || []).join(', ');
        deliveryPrimaryLine = st ? `Not shipped to ${this.escapeHTML(st)}` : 'In-store pickup only';
        deliverySubLine = 'Available for store pickup';
      } else if (deliveryDate) {
        deliveryPrimaryLine = `As soon as <strong>${this.escapeHTML(deliveryDate)}</strong>`;
      } else if (isNoGround) {
        if (this._deliveryResult) {
          deliveryPrimaryLine = deliveryZip
            ? `<strong>In-store pickup only</strong> at ${this.escapeHTML(deliveryZip)}`
            : '<strong>In-store pickup only</strong> at this address';
          deliverySubLine = 'Outside 50-mile local delivery area';
        } else if (this.dataset.specialOrder === 'true') {
          deliveryPrimaryLine = `<strong>${this.escapeHTML(this.dataset.specialOrderMessage || 'Ships in 3–5 days')}</strong>`;
          deliverySubLine = 'Local truck delivery (within 50 miles)';
        } else {
          deliveryPrimaryLine = 'Local truck delivery available';
          deliverySubLine = 'Local truck delivery (within 50 miles)';
        }
      }

      const storeIconSvg = `<svg class="fulfillment-options__card-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9l1.5-5h15L21 9"/><path d="M3 9c0 1.66 1.34 3 3 3s3-1.34 3-3c0 1.66 1.34 3 3 3s3-1.34 3-3c0 1.66 1.34 3 3 3s3-1.34 3-3"/><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8"/><path d="M9 21v-6h6v6"/></svg>`;
      const truckIconSvg = `<svg class="fulfillment-options__card-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="3" width="15" height="13" rx="1"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>`;
      const noGroundBannerHtml = showNoGroundBanner ? `
        <div class="fulfillment-options__no-ground-banner" role="note">
          <span class="fulfillment-options__no-ground-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1.5-5h15L21 9"/><path d="M3 9c0 1.66 1.34 3 3 3s3-1.34 3-3c0 1.66 1.34 3 3 3s3-1.34 3-3c0 1.66 1.34 3 3 3s3-1.34 3-3"/><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8"/><path d="M9 21v-6h6v6"/></svg>
          </span>
          <span class="fulfillment-options__no-ground-title">Available for store pickup or local delivery only</span>
        </div>
      ` : '';

      let closestInStockHtml = '';
      if (!selectedHasStock && fasterCount > 0) {
        const nearestList = this.getNearestInStockHandles(inStockHandles, selectedStore, 1);
        const nearestEntry = nearestList[0] || (this.getClosestInStockHandle(inStockHandles) ? { handle: this.getClosestInStockHandle(inStockHandles), dist: NaN } : null);
        if (nearestEntry && nearestEntry.handle) {
          const nearestMeta = this.getStoreMeta(nearestEntry.handle);
          const nearestName = nearestMeta?.name || this.formatHandle(nearestEntry.handle);
          const distText = Number.isFinite(nearestEntry.dist)
            ? ` (${this.formatDistance(nearestEntry.dist)})`
            : (nearestMeta?.distance ? ` (${nearestMeta.distance})` : '');
          closestInStockHtml = `
            <p class="fulfillment-options__closest-note" style="margin:-0.25rem 0 0;padding:0 1rem 0.85rem;font-size:0.8125rem;line-height:1.4;color:#374151;">
              Closest store in stock today: <strong>${this.escapeHTML(nearestName)}</strong>${this.escapeHTML(distText)} &mdash;
              <button type="button" class="fulfillment-options__inline-store-btn" data-set-fulfillment-store="${this.escapeHTML(nearestEntry.handle)}" style="background:none;border:0;padding:0;color:#1B4332;font-weight:600;text-decoration:underline;cursor:pointer;font:inherit;">Switch to ${this.escapeHTML(nearestName)}</button>
              or
              <button type="button" class="fulfillment-options__inline-store-btn" data-open-store-drawer style="background:none;border:0;padding:0;color:#1B4332;font-weight:600;text-decoration:underline;cursor:pointer;font:inherit;">change location</button>
            </p>
          `;
        }
      }

      const defaultDeliveryHeading = isNoGround ? 'Local Delivery' : 'Delivery';

      this.innerHTML = `
        <section class="fulfillment-options" aria-label="Fulfillment options">
          ${noGroundBannerHtml}
          <div class="fulfillment-options__location-row">
            <button type="button" class="fulfillment-options__location" data-open-store-drawer>
              <span class="fulfillment-options__location-label">Store at</span>
              <span class="fulfillment-options__location-value">${this.escapeHTML(storeHeaderText)}</span>
            </button>
            <button type="button" class="fulfillment-options__location" data-open-delivery-drawer>
              <span class="fulfillment-options__location-label">${isNoGround ? 'Local delivery to' : 'Delivery to'}</span>
              <span class="fulfillment-options__location-value" data-delivery-zip>${this.escapeHTML(deliveryZip)}</span>
            </button>
          </div>

          <div class="fulfillment-options__method-grid" role="group" aria-label="Fulfillment methods" style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));align-items:stretch;gap:0.75rem;">
            <button type="button" class="fulfillment-options__method-card fulfillment-options__method-card--button${pickupSelected ? ' fulfillment-options__method-card--selected' : ''}" data-select-fulfillment="pickup" aria-pressed="${pickupSelected ? 'true' : 'false'}" style="height:100%;min-height:5.6rem;display:flex;flex-direction:column;justify-content:flex-start;box-sizing:border-box;">
              ${pickupSelected ? '<span class="fulfillment-options__corner-badge" aria-hidden="true"><span>&#10003;</span></span>' : ''}
              <div class="fulfillment-options__card-body">
                <div class="fulfillment-options__method-top">
                  ${storeIconSvg}
                  <span class="fulfillment-options__method-heading">Pickup</span>
                </div>
                <p class="fulfillment-options__primary-line">${pickupPrimaryLine}</p>
                <span class="fulfillment-options__sub-line">${this.escapeHTML(pickupSubLine)}</span>
              </div>
            </button>

            <button type="button" class="fulfillment-options__method-card fulfillment-options__method-card--button${deliverySelected ? ' fulfillment-options__method-card--selected' : ''}" data-select-fulfillment="delivery" data-delivery-type="standard" aria-pressed="${deliverySelected ? 'true' : 'false'}" style="height:100%;min-height:5.6rem;display:flex;flex-direction:column;justify-content:flex-start;box-sizing:border-box;">
              ${deliverySelected ? '<span class="fulfillment-options__corner-badge" aria-hidden="true"><span>&#10003;</span></span>' : ''}
              <div class="fulfillment-options__card-body">
                <div class="fulfillment-options__method-top">
                  ${truckIconSvg}
                  <span class="fulfillment-options__method-heading">${deliverySelected && this._deliveryChoiceMethod ? this.escapeHTML(this._deliveryChoiceMethod) : defaultDeliveryHeading}</span>
                </div>
                <p class="fulfillment-options__primary-line">${deliverySelected && this._deliveryChoiceDate ? `<strong>${this.escapeHTML(this._deliveryChoiceDate)}</strong>` : deliveryPrimaryLine}</p>
                <span class="fulfillment-options__sub-line">${deliverySubLine}</span>
              </div>
            </button>
          </div>
          ${closestInStockHtml}
        </section>

        ${this.renderAvailabilityDrawer({ selectedStore, locations, hasStockData, fasterCount })}
        ${this.renderDeliveryDrawer({ selectedStore })}
      `;

      this.bindFulfillmentPanelActions();
      this.syncFulfillmentProperties();
      this.ensureDefaultDeliveryQuote(selectedStore, deliveryZip);
    }

    renderPickupChoices({ showPickupChoices, nearby, selectedStore, selectedMeta, locations }) {
      if (!showPickupChoices || !nearby || !nearby.length) return '';

      const qty = this.getSelectedQuantity();
      const homeName = selectedMeta ? selectedMeta.name : this.formatHandle(selectedStore);
      const groupName = `pickup-choice-${this.escapeHTML(this.dataset.id || 'product')}`;

      const nearbyRows = nearby.map((n) => {
        const meta = this.getStoreMeta(n.handle);
        const distance = Number.isFinite(n.dist) ? ` (${this.formatDistance(n.dist)})` : '';
        const when = this.getInStockPickupLabel(n.handle);
        return `
          <label class="fulfillment-options__choice" data-pickup-choice="nearby" data-handle="${this.escapeHTML(n.handle)}">
            <input type="radio" name="${groupName}" value="nearby-${this.escapeHTML(n.handle)}">
            <span class="fulfillment-options__choice-radio" aria-hidden="true"></span>
            <span class="fulfillment-options__choice-body">
              <span class="fulfillment-options__choice-title">Pickup Nearby at ${this.escapeHTML(meta.name)}${distance}</span>
              <span class="fulfillment-options__choice-note fulfillment-options__choice-note--today">Pickup ${this.escapeHTML(qty)} ${this.escapeHTML(when)} &middot; FREE</span>
            </span>
          </label>
        `;
      }).join('');

      return `
        <fieldset class="fulfillment-options__pickup-choices">
          <legend class="visually-hidden">Choose a pickup option</legend>
          ${nearbyRows}
          <label class="fulfillment-options__choice fulfillment-options__choice--selected" data-pickup-choice="home">
            <input type="radio" name="${groupName}" value="home" checked>
            <span class="fulfillment-options__choice-radio" aria-hidden="true"></span>
            <span class="fulfillment-options__choice-body">
              <span class="fulfillment-options__choice-title">Pickup Later at ${this.escapeHTML(homeName)}</span>
              <span class="fulfillment-options__choice-note">Pickup ${this.escapeHTML(qty)} later ${this.escapeHTML(this.getPickupEtaPhrase(selectedStore))} &middot; FREE</span>
            </span>
          </label>
        </fieldset>
      `;
    }

    getProductForm() {
      return this.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    }

    // The shopper's selected store, or the default store (Nassau) so the panel
    // always has a concrete store context. Returns null only if no stores load.
    getEffectiveStore() {
      const selected = localStorage.getItem('selected-store');
      const list = this.storeSelector?.storesList || {};
      if (selected && list[selected]) return selected;
      if (list[PickupAvailabilityCompact.DEFAULT_STORE]) return PickupAvailabilityCompact.DEFAULT_STORE;
      const handles = Object.keys(list);
      return handles[0] || null;
    }

    // Short "City, ST" label for the location header (falls back to store name).
    getStoreLocationLabel(handle) {
      if (!handle) return 'Select a store';
      const meta = this.getStoreMeta(handle);
      const match = (meta.address || '').match(/([A-Za-z .'\-]+),\s*([A-Z]{2})\b/);
      if (match) return `${match[1].trim()}, ${match[2]}`;
      return meta.name || this.formatHandle(handle);
    }

    // Pull a US ZIP from an address string: prefer the digits right after the
    // 2-letter state (avoids matching a 5-digit street number), else the last
    // 5-digit group.
    extractZip(str) {
      const text = String(str || '');
      const afterState = text.match(/[A-Z]{2}\s*(\d{5})\b/);
      if (afterState) return afterState[1];
      const all = text.match(/\b\d{5}\b/g);
      return all ? all[all.length - 1] : '';
    }

    getStoreZip(handle) {
      const meta = handle ? this.getStoreMeta(handle) : null;
      return this.extractZip(meta?.address);
    }

    // ZIP for the "Delivery to" header: the entered delivery address ZIP if any,
    // otherwise the saved delivery ZIP or the store's ZIP as a sensible local default.
    getDeliveryZip(handle) {
      let savedZip = '';
      try { savedZip = localStorage.getItem('hatchet_delivery_zip') || ''; } catch (_) {}
      return this.extractZip(this._deliveryAddress) || savedZip || this.getStoreZip(handle);
    }

    // Fastest delivery date from a /apps/roo/delivery-options result across
    // same-day, FedEx ground, and local truck delivery.
    getEarliestDeliveryDate(result) {
      if (!result) return null;
      const isNoGround = this.dataset.noGround === 'true' || !!(result.fedex && result.fedex.no_ground_shipping);
      const candidates = [];
      const sameDay = this.getSameDayInfo(result);
      if (sameDay.eligible && sameDay.offer) {
        candidates.push({
          ymd: sameDay.offer.delivery_date || '0000-00-00',
          label: (sameDay.offer.display_date || 'today').replace(/^Today$/i, 'today')
        });
      }
      const fedex = result.fedex || {};
      const services = Array.isArray(fedex.services) ? fedex.services : [];
      if (!isNoGround && fedex.available && services.length) {
        const best = services[0] || {};
        const cleanLabel = String(best.display_date || best.display_text || '').replace(/^Get it by\s+/i, '').trim();
        if (cleanLabel) {
          candidates.push({
            ymd: best.delivery_date || '9999-99-99',
            label: cleanLabel
          });
        }
      }
      const local = result.local_delivery || {};
      if (local.available && (local.display_date || local.earliest_date)) {
        candidates.push({
          ymd: local.earliest_date || '9999-99-99',
          label: local.display_date || local.earliest_date
        });
      }
      if (!candidates.length) return null;
      candidates.sort((a, b) => a.ymd.localeCompare(b.ymd));
      return candidates[0].label;
    }

    getDeliverySubLine(result) {
      const isNoGround = this.dataset.noGround === 'true' || !!(result && result.fedex && result.fedex.no_ground_shipping);
      if (!result || result.restricted_state) {
        if (isNoGround) {
          return 'Local truck delivery (within 50 miles)';
        }
        if (this.dataset.specialOrder === 'true') {
          return 'Ground &amp; Local Truck delivery';
        }
        return 'Ground, Same-Day &amp; Local Truck options';
      }
      const methods = [];
      const fedex = result.fedex || {};
      if (!isNoGround && fedex.available) {
        methods.push(fedex.free_ground_shipping ? 'Free Ground' : 'Ground');
      }
      const sameDay = this.getSameDayInfo(result);
      if (sameDay.eligible && sameDay.offer) {
        const price = sameDay.offer.price ? ` (${this.escapeHTML(sameDay.offer.price)})` : '';
        methods.push(`Same-Day${price}`);
      }
      const local = result.local_delivery || {};
      if (local.available) {
        methods.push('Local Truck');
      }
      if (isNoGround) {
        if (methods.length === 2) return `${methods[0]} &amp; ${methods[1]} delivery`;
        if (methods.length === 1) return local.available ? 'Local Truck delivery only' : `${methods[0]} delivery only`;
        return 'Outside 50-mile local delivery area';
      }
      if (methods.length === 3) return `${methods[0]}, ${methods[1]} &amp; ${methods[2]}`;
      if (methods.length === 2) return `${methods[0]} &amp; ${methods[1]} delivery`;
      if (methods.length === 1) return `${methods[0]} delivery available`;
      return 'Shipping &amp; delivery options in cart';
    }

    ensureDefaultDeliveryQuote(selectedStore, deliveryZip) {
      const variantId = this.dataset.variantId;
      if (!variantId || !deliveryZip) return;
      const qty = this.getSelectedQuantity();
      const quoteKey = `${variantId}|${selectedStore || ''}|${deliveryZip}|${qty}`;
      if (this._autoDeliveryQuoteKey === quoteKey && this._deliveryResult) return;
      this._autoDeliveryQuoteKey = quoteKey;

      let point = { postal_code: deliveryZip, country: 'US' };
      try {
        const savedPointRaw = localStorage.getItem('hatchet_delivery_point');
        if (savedPointRaw) {
          const parsed = JSON.parse(savedPointRaw);
          if (parsed && String(parsed.postal_code || '').slice(0, 5) === String(deliveryZip).slice(0, 5)) {
            point = parsed;
          }
        }
      } catch (_) {}

      if (!point.lat && selectedStore && this.getStoreZip(selectedStore) === deliveryZip) {
        const card = this.getStoreCard(selectedStore);
        const lat = parseFloat(card?.dataset.lat);
        const lng = parseFloat(card?.dataset.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          point.lat = lat;
          point.lng = lng;
          point.province = 'NY';
        }
      }

      const params = this.buildDeliveryOptionsParams(point);
      if (!params) return;

      fetch(`/apps/roo/delivery-options?${params.toString()}`)
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (!data || this._autoDeliveryQuoteKey !== quoteKey) return;
          this._deliveryResult = data;
          this.updateDeliveryCard();
        })
        .catch(() => {});
    }

    // Same-day weight cap: this product line only (variant weight × quantity).
    // PDP can't see the whole cart, so we gate on the line. null = unknown weight.
    // MUST match carrier_service_same_day_max_weight_lbs on the backend — a lower
    // value here tells the shopper they're ineligible for a cart the rate engine
    // would happily quote.
    static SAME_DAY_MAX_LB = 360;

    getLineWeightLb() {
      const grams = Number(this.dataset.variantWeightGrams);
      if (!Number.isFinite(grams) || grams <= 0) return null;
      return (grams * Number(this.getSelectedQuantity())) / 453.59237;
    }

    // Same-day eligibility for display. The backend (Roo) already gates on the
    // ≤15-min in-stock rule and returns same_day_delivery; here we only layer on
    // the theme-side weight cap.
    getSameDayInfo(result) {
      const offer = (result && result.same_day_delivery) || null;
      const lineLb = this.getLineWeightLb();
      const overWeight = lineLb != null && lineLb > PickupAvailabilityCompact.SAME_DAY_MAX_LB;
      return { offer, overWeight, eligible: !!(offer && offer.available) && !overWeight };
    }

    renderSameDayCardInner({ sameDay, sameDaySelected, hasResult, variantAvailable }) {
      const maxNote = `Max ${PickupAvailabilityCompact.SAME_DAY_MAX_LB} lbs`;
      let body;
      let price = '';
      if (!variantAvailable) {
        body = 'Unavailable for this variant.';
      } else if (sameDay.overWeight) {
        body = `Over ${PickupAvailabilityCompact.SAME_DAY_MAX_LB} lb &mdash; not eligible`;
      } else if (!hasResult) {
        body = 'Enter Address';
      } else if (sameDay.eligible) {
        const offer = sameDay.offer || {};
        body = `<strong>${this.escapeHTML(offer.display_date || 'Today')}</strong>`;
        if (offer.price) price = `<strong class="fulfillment-options__price">${this.escapeHTML(offer.price)}</strong>`;
      } else {
        body = 'Not available at your address';
      }

      return `
              <div class="fulfillment-options__method-top">
                <span class="fulfillment-options__method-heading">Same-Day Delivery</span>
                ${sameDaySelected ? '<span class="fulfillment-options__method-check" aria-hidden="true"></span>' : ''}
              </div>
              <p>${body}</p>
              ${price}
              <span class="fulfillment-options__max-note">${maxNote}</span>`;
    }

    renderSameDayCard(state) {
      return `
            <button type="button" class="fulfillment-options__method-card fulfillment-options__method-card--button${state.sameDaySelected ? ' fulfillment-options__method-card--selected' : ''}" data-open-delivery-drawer data-delivery-type="same_day">${this.renderSameDayCardInner(state)}
            </button>`;
    }

    // Body text for the standard Delivery card. For a no-ground (oversize) item the backend
    // suppresses FedEx, so `date` is a LOCAL delivery date when available; if none is available for
    // the entered address the item is pickup-only — say so instead of a dead-end "enter your address"
    // prompt that can never resolve. Shared by render() and updateDeliveryCard() so they stay in sync.
    deliveryCardBody(variantAvailable, date) {
      if (!variantAvailable) return 'Unavailable for this variant.';
      // State restriction: the backend refuses to quote (restricted_state:true) — say why
      // instead of a dead-end prompt. Before an address is entered, the dataset hint from
      // the metafield covers the same message.
      if (this._deliveryResult && this._deliveryResult.restricted_state) {
        const st = (this._deliveryResult.restricted_states || []).join(', ');
        return st ? `Cannot be shipped to ${this.escapeHTML(st)} — in-store pickup only` : 'Enter your full address including state';
      }
      if (date) return `As soon as <strong>${this.escapeHTML(date)}</strong>`;
      if (this.dataset.noGround === 'true' || (this._deliveryResult && this._deliveryResult.fedex && this._deliveryResult.fedex.no_ground_shipping)) {
        const zip = this.getDeliveryZip(this.getEffectiveStore());
        return this._deliveryResult
          ? (zip ? `<strong>In-store pickup only</strong> at ${this.escapeHTML(zip)}` : '<strong>In-store pickup only</strong> at this address')
          : 'Local truck delivery available';
      }
      if (!this._deliveryResult && this.dataset.restrictedStates) {
        return `Enter your address for a delivery date (not available in ${this.escapeHTML(this.dataset.restrictedStates.split(',').join(', '))})`;
      }
      return 'Enter your address for a delivery date';
    }

    // Keep the Delivery card + "Delivery to" ZIP in sync after an address-driven
    // delivery result, without a full panel re-render (avoids drawer churn).
    updateDeliveryCard() {
      this.syncRestrictedNotice();
      const isNoGround = this.dataset.noGround === 'true' || !!(this._deliveryResult && this._deliveryResult.fedex && this._deliveryResult.fedex.no_ground_shipping);
      if (isNoGround && this.dataset.noGround !== 'true') {
        this.dataset.noGround = 'true';
      }
      const variantAvailable = this.dataset.variantAvailable !== 'false';
      const date = variantAvailable ? this.getEarliestDeliveryDate(this._deliveryResult) : null;

      // Ensure top Option A-Unified banner + Local Delivery labels stay synced if backend reports no_ground_shipping.
      const sectionEl = this.querySelector('.fulfillment-options');
      if (sectionEl && isNoGround && this.dataset.deliveryTerms !== '1' && !sectionEl.querySelector('.fulfillment-options__no-ground-banner')) {
        const banner = document.createElement('div');
        banner.className = 'fulfillment-options__no-ground-banner';
        banner.setAttribute('role', 'note');
        banner.innerHTML = `
          <span class="fulfillment-options__no-ground-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1.5-5h15L21 9"/><path d="M3 9c0 1.66 1.34 3 3 3s3-1.34 3-3c0 1.66 1.34 3 3 3s3-1.34 3-3c0 1.66 1.34 3 3 3s3-1.34 3-3"/><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8"/><path d="M9 21v-6h6v6"/></svg>
          </span>
          <span class="fulfillment-options__no-ground-title">Available for store pickup or local delivery only</span>
        `;
        sectionEl.insertBefore(banner, sectionEl.firstChild);
      }
      const deliveryLocBtn = this.querySelector('[data-open-delivery-drawer] .fulfillment-options__location-label');
      if (deliveryLocBtn && isNoGround) {
        deliveryLocBtn.textContent = 'Local delivery to';
      }

      // Standard Delivery card (target by type — the same-day card also has data-open-delivery-drawer).
      const card = this.querySelector('.fulfillment-options__method-card[data-delivery-type="standard"]');
      const headingEl = card?.querySelector('.fulfillment-options__method-heading');
      if (headingEl && isNoGround && !(this._fulfillmentMethod === 'delivery' && this._deliveryChoiceMethod)) {
        headingEl.textContent = 'Local Delivery';
      }
      const line = card?.querySelector('.fulfillment-options__primary-line, p');
      if (line && !(this._fulfillmentMethod === 'delivery' && this._deliveryChoiceDate)) {
        line.innerHTML = this.deliveryCardBody(variantAvailable, date);
      }
      const subLineEl = card?.querySelector('.fulfillment-options__sub-line');
      if (subLineEl && variantAvailable) {
        subLineEl.innerHTML = this.getDeliverySubLine(this._deliveryResult);
      }

      // Same-Day card — rebuild its contents in place (keeps the element + its drawer binding).
      const sameDayCard = this.querySelector('.fulfillment-options__method-card[data-delivery-type="same_day"]');
      if (sameDayCard) {
        const method = this._fulfillmentMethod === 'delivery' ? 'delivery' : 'pickup';
        const deliveryType = this._deliveryChoiceType === 'same_day' ? 'same_day' : 'standard';
        const sameDaySelected = method === 'delivery' && deliveryType === 'same_day';
        const sameDay = variantAvailable ? this.getSameDayInfo(this._deliveryResult) : { offer: null, overWeight: false, eligible: false };
        sameDayCard.innerHTML = this.renderSameDayCardInner({ sameDay, sameDaySelected, hasResult: !!this._deliveryResult, variantAvailable });
        sameDayCard.classList.toggle('fulfillment-options__method-card--selected', sameDaySelected);
      }

      const zipEl = this.querySelector('[data-delivery-zip]');
      if (zipEl) zipEl.textContent = this.getDeliveryZip(this.getEffectiveStore());
    }

    // Keep the cart line-item properties in step with the chosen fulfillment method.
    // Pickup → attach "Pickup Store" (only when a store is actually picked, not the
    // Nassau display default). Delivery → attach "Delivery" and drop the pickup store
    // so a delivery order is never labelled with a pickup store.
    syncFulfillmentProperties() {
      const form = this.getProductForm();
      const storeInput = form?.querySelector('[data-pickup-store-property]');
      const deliveryInput = form?.querySelector('[data-delivery-property]');
      const readyByInput = form?.querySelector('[data-ready-by-property]');
      const method = this._fulfillmentMethod === 'delivery' ? 'delivery' : 'pickup';

      const setInput = (input, value) => {
        if (!input) return;
        if (value) { input.value = value; input.disabled = false; }
        else { input.value = ''; input.disabled = true; }
      };

      const selectedStore = localStorage.getItem('selected-store');
      const storeName = selectedStore && this.storeSelector?.storesList?.[selectedStore];

      // Named for what the customer actually gets: a pickup is "Ready for Pickup",
      // a delivery is "Ship By" (transit still follows). Both carry a LEADING
      // UNDERSCORE, which keeps them on the admin order for staff but hides them
      // from the customer — the checkout extension already shows this date live and
      // method-aware, so the frozen line property was duplicating it and could
      // contradict it once the buyer switched Ship/Pickup at checkout.
      const setDateInput = (label, value) => {
        if (!readyByInput) return;
        if (value) readyByInput.name = `properties[${label}]`;
        setInput(readyByInput, value);
      };

      // Only items that aren't already on the shelf carry a date. An in-stock line
      // would just stamp "today" on every order, which is noise for the store and
      // the customer. No lead time known → no property at all.
      const dateFor = (handle) => {
        const lt = handle && this._stockData?.leadTimes?.[handle];
        return lt && lt.inStock === false ? this.getTransferDate(handle) : null;
      };

      if (method === 'delivery') {
        setInput(deliveryInput, null);
        setInput(storeInput, null);
        // Hatchet Farm ships from NASSAU, not from the browsing store — so a
        // delivery quotes Nassau's date. Using the selected pickup store's would
        // add a store-transfer hop that a shipped order never makes.
        setDateInput('_Ship By', dateFor(SHIP_FROM_STORE_HANDLE));
      } else {
        setInput(storeInput, storeName || null);
        setInput(deliveryInput, null);
        setDateInput('_Ready for Pickup', storeName ? dateFor(selectedStore) : null);
      }
    }

    // "usually ready by <date>", or a plain range when no date can be worked out — never a
    // dangling "usually ready by" with nothing after it.
    readyByText(handle) {
      const date = this.getTransferDate(handle);
      return date ? `usually ready by ${date}` : 'usually ready in 3-5 business days';
    }

    getStoreAvailability(handle, locations, hasStockData) {
      if (!handle) {
        return {
          mode: 'choose',
          label: 'Choose Store',
          inventoryLine: 'Choose a store to see pickup timing.',
          drawerLine: 'Choose a store to see pickup timing.'
        };
      }

      if (!hasStockData) {
        return {
          mode: 'checking',
          label: 'Checking Availability',
          inventoryLine: 'Checking store inventory.',
          drawerLine: 'Checking availability.'
        };
      }

      if (locations[handle] === true) {
        const when = this.getInStockPickupLabel(handle);
        return {
          mode: 'in-stock',
          label: 'In Stock',
          inventoryLine: `Available for pickup ${when}.`,
          drawerLine: `Get it ${when}.`
        };
      }

      // No backend lead time for this item at all — it's on no shelf, not at BCI,
      // and its vendor has no direct-ship cutoff. Interpolating the empty date
      // here is what produced the literal "Ships to store. Get it ." on the PDP.
      const transferDate = this.getTransferDate(handle);
      if (!transferDate) {
        return {
          mode: 'ships-to-store',
          label: 'Ships to Store',
          inventoryLine: 'Ships to store. Usually 3–5 business days.',
          drawerLine: 'Usually 3–5 business days.'
        };
      }

      return {
        mode: 'ships-to-store',
        label: 'Ships to Store',
        inventoryLine: `Ships to store. Get it ${transferDate}.`,
        drawerLine: `Get it ${transferDate}.`
      };
    }

    renderAvailabilityDrawer({ selectedStore, locations, hasStockData, fasterCount }) {
      const selectedMeta = selectedStore ? this.getStoreMeta(selectedStore) : null;
      const selectedAvailability = selectedStore ? this.getStoreAvailability(selectedStore, locations, hasStockData) : null;
      const sortEarliest = this._storeDrawerSortEarliest !== false;
      const nearbyHandles = this.getSortedStoreHandles(locations, { sortEarliest }).filter(handle => handle !== selectedStore);
      const productTitle = this.dataset.productTitle || 'Selected item';
      const productImage = this.dataset.productImage;
      const fasterLabel = fasterCount > 0 ? `${fasterCount} ${fasterCount === 1 ? 'store has' : 'stores have'} this in stock today` : 'Nearby store availability';
      const sortLabelId = `fulfillment-store-sort-${this.escapeHTML(this.dataset.id || 'product')}`;

      return `
        <div class="fulfillment-store-drawer" data-store-availability-drawer data-owner="${this.escapeHTML(this.dataset.id || 'product')}" hidden aria-hidden="true">
          <button type="button" class="fulfillment-store-drawer__scrim" data-close-store-drawer aria-label="Close nearby stores"></button>
          <aside class="fulfillment-store-drawer__panel" role="dialog" aria-modal="true" aria-label="Check Nearby Stores">
            <div class="fulfillment-store-drawer__header">
              <h2>Check Nearby Stores</h2>
              <button type="button" class="fulfillment-store-drawer__close" data-close-store-drawer aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="fulfillment-store-drawer__product">
              ${productImage ? `<img src="${this.escapeHTML(productImage)}" alt="" loading="lazy">` : '<span class="fulfillment-store-drawer__product-placeholder" aria-hidden="true"></span>'}
              <strong>${this.escapeHTML(productTitle)}</strong>
            </div>

            ${selectedMeta ? `
              <div class="fulfillment-store-drawer__current">
                <span>Currently Shopping</span>
                ${this.renderDrawerStoreRow(selectedStore, selectedMeta, selectedAvailability, true)}
              </div>
            ` : ''}

            <div class="fulfillment-store-drawer__nearby-heading">
              <h3>Nearby Stores</h3>
              <span>${this.escapeHTML(fasterLabel)}</span>
            </div>

            <div class="fulfillment-store-drawer__search">
              <form class="fulfillment-store-drawer__search-form" data-store-drawer-search>
                <label class="visually-hidden" for="fulfillment-store-search-${this.escapeHTML(this.dataset.id || 'product')}">ZIP or city and state</label>
                <input
                  id="fulfillment-store-search-${this.escapeHTML(this.dataset.id || 'product')}"
                  type="search"
                  inputmode="search"
                  autocomplete="postal-code"
                  placeholder="ZIP or city and state"
                  value="${this.escapeHTML(this._drawerLocationQuery || '')}"
                  data-store-drawer-input
                >
                <button type="submit">Search</button>
              </form>
              <div class="fulfillment-store-drawer__suggestions" data-store-drawer-suggestions hidden></div>
              <button type="button" class="fulfillment-store-drawer__locate" data-store-drawer-locate>
                Use current location
              </button>
              <p class="fulfillment-store-drawer__status" data-store-drawer-status>${this.escapeHTML(this._drawerLocationStatus || '')}</p>
            </div>

            <div class="fulfillment-store-drawer__sort" aria-label="Store sorting">
              <span id="${sortLabelId}">Sort by earliest available</span>
              <button
                type="button"
                class="fulfillment-store-drawer__toggle${sortEarliest ? ' is-on' : ''}"
                aria-labelledby="${sortLabelId}"
                aria-pressed="${sortEarliest ? 'true' : 'false'}"
                data-store-drawer-sort-toggle
              ></button>
            </div>

            <div class="fulfillment-store-drawer__stores">
              ${this.renderDrawerNearbyStoreRows({ nearbyHandles, locations, hasStockData })}
            </div>
          </aside>
        </div>
      `;
    }

    renderDrawerStoreRow(handle, meta, availability, isSelected) {
      const distance = meta.distance ? `<span class="fulfillment-store-drawer__distance">${this.escapeHTML(meta.distance)}</span>` : '';
      const address = meta.address ? `<p>${this.escapeHTML(meta.address)}</p>` : '';
      const statusText = meta.statusText ? `<p class="fulfillment-store-drawer__hours">${this.escapeHTML(meta.statusText)}</p>` : '';
      const action = isSelected
        ? '<span class="fulfillment-store-drawer__selected">Currently Shopping</span>'
        : `<button type="button" class="fulfillment-store-drawer__change" data-set-fulfillment-store="${this.escapeHTML(handle)}">Change Store</button>`;

      return `
        <article class="fulfillment-store-drawer__store fulfillment-store-drawer__store--${this.escapeHTML(availability.mode)}">
          <div class="fulfillment-store-drawer__store-top">
            <h4>${this.escapeHTML(meta.name)}</h4>
            ${distance}
          </div>
          ${address}
          ${statusText}
          <div class="fulfillment-store-drawer__availability">
            <span aria-hidden="true"></span>
            <strong>${this.escapeHTML(availability.label)}</strong>
            ${this.escapeHTML(availability.drawerLine)}
          </div>
          ${action}
        </article>
      `;
    }

    renderDeliveryDrawer({ selectedStore }) {
      const result = this._deliveryResult;

      return `
        <div class="fulfillment-delivery-drawer" data-delivery-drawer data-owner="${this.escapeHTML(this.dataset.id || 'product')}" hidden aria-hidden="true">
          <button type="button" class="fulfillment-delivery-drawer__scrim" data-close-delivery-drawer aria-label="Close delivery address"></button>
          <aside class="fulfillment-delivery-drawer__panel" role="dialog" aria-modal="true" aria-label="Delivery Address">
            <div class="fulfillment-delivery-drawer__header">
              <h2>Delivery Address</h2>
              <button type="button" class="fulfillment-delivery-drawer__close" data-close-delivery-drawer aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <form class="fulfillment-delivery-drawer__form" data-delivery-address-form>
              <label for="fulfillment-delivery-address-${this.escapeHTML(this.dataset.id || 'product')}">Street address, city, state, and ZIP</label>
              <div class="fulfillment-delivery-drawer__autocomplete">
                <div class="fulfillment-delivery-drawer__input-row">
                  <input
                    id="fulfillment-delivery-address-${this.escapeHTML(this.dataset.id || 'product')}"
                    type="search"
                    autocomplete="off"
                    placeholder="e.g., 123 Your Street, Chicago, IL 00000"
                    value="${this.escapeHTML(this._deliveryAddress || '')}"
                    data-delivery-address-input
                  >
                  <button type="button" data-clear-delivery-address aria-label="Clear delivery address">&times;</button>
                </div>
                <div class="fulfillment-delivery-drawer__suggestions" data-delivery-address-suggestions hidden></div>
              </div>
              <button type="submit" class="fulfillment-delivery-drawer__submit">Check Delivery</button>
              <p class="fulfillment-delivery-drawer__status" data-delivery-address-status>${this.escapeHTML(this._deliveryStatus || '')}</p>
            </form>

            <div data-delivery-result-region>
              ${result ? this.renderDeliveryResult(result) : ''}
            </div>
          </aside>
        </div>
      `;
    }

    // A selectable "standard" delivery option in the drawer. Both Local (truck) and FedEx
    // are "standard" — the choice just stores a display label/date; checkout still rates it.
    _deliverySelectButton(method, dateText) {
      const label = `${method} — ${dateText}`;
      return `
        <button type="button" class="fulfillment-delivery-drawer__store-block fulfillment-delivery-drawer__select" data-select-delivery data-delivery-type="standard" data-method="${this.escapeHTML(method)}" data-date="${this.escapeHTML(dateText)}" data-label="${this.escapeHTML(label)}">
          <span>${this.escapeHTML(method)}</span>
          <p class="fulfillment-delivery-drawer__success">${this.escapeHTML(dateText)}</p>
          <span class="fulfillment-delivery-drawer__select-cta">Choose ${this.escapeHTML(method)}</span>
        </button>
      `;
    }

    renderDeliveryResult(result) {
      // State restriction: the backend refused to quote. Return before any method block can
      // render so no quote markup leaks; pickup renders from Shopify's native
      // pickup-availability-data, untouched.
      if (result.restricted_state) {
        return `
          <div class="fulfillment-delivery-drawer__result">
            <div class="fulfillment-delivery-drawer__store-block">
              <p><strong>Notice:</strong> ${this.escapeHTML(result.message || 'This item cannot be shipped to your state.')}</p>
              <p>In-store pickup is still available.</p>
            </div>
          </div>
        `;
      }
      const local = result.local_delivery || {};
      const fedex = result.fedex || {};
      const isNoGround = this.dataset.noGround === 'true' || !!(fedex && fedex.no_ground_shipping);
      const sameDay = this.getSameDayInfo(result);
      const sameDayBlock = this.renderSameDayDrawerBlock(sameDay);

      // Show every available method side by side: Same-Day, Local/truck delivery, AND FedEx
      // Ground shipping (the standard next-business-day option), rather than only one.
      const blocks = [];
      const localDate = local.display_date || local.earliest_date;
      if (local.available && localDate) {
        blocks.push(this._deliverySelectButton('Local delivery', `As soon as ${localDate}`));
      }
      const fedexServices = Array.isArray(fedex.services) ? fedex.services : [];
      if (!isNoGround && fedex.available && fedexServices.length) {
        const best = fedexServices[0] || {};
        const text = best.display_text
          || (best.delivery_date ? `Get it by ${best.delivery_date}` : 'Delivery date confirmed at checkout');
        blocks.push(this._deliverySelectButton('FedEx Ground shipping', text));
      }

      if (!blocks.length) {
        if (isNoGround && !sameDay.eligible) {
          return `
            <div class="fulfillment-delivery-drawer__result">
              ${sameDayBlock}
              <div class="fulfillment-delivery-drawer__store-block">
                <p><strong>Outside our 50-mile local delivery area.</strong></p>
                <p>Available for store pickup or local delivery only.</p>
              </div>
            </div>
          `;
        }
        return `
          <div class="fulfillment-delivery-drawer__result">
            ${sameDayBlock}
            <div class="fulfillment-delivery-drawer__store-block">
              <p>Delivery options will be confirmed at checkout.</p>
            </div>
          </div>
        `;
      }

      return `
        <div class="fulfillment-delivery-drawer__result">
          ${sameDayBlock}
          ${blocks.join('')}
        </div>
      `;
    }

    // Same-day option shown atop the delivery drawer. Selectable when eligible;
    // a non-selectable info row when the line exceeds the weight cap.
    renderSameDayDrawerBlock(sameDay) {
      const max = PickupAvailabilityCompact.SAME_DAY_MAX_LB;
      if (sameDay.overWeight) {
        return `
          <div class="fulfillment-delivery-drawer__store-block fulfillment-delivery-drawer__store-block--disabled">
            <span>Same-Day Delivery</span>
            <p>Exceeds ${max} lb limit for same-day.</p>
          </div>
        `;
      }
      if (!sameDay.eligible) return '';

      const offer = sameDay.offer || {};
      const title = this.escapeHTML(offer.label || 'Same-Day Delivery');
      const when = this.escapeHTML(offer.display_date || 'Today');
      const price = offer.price ? this.escapeHTML(offer.price) : '';
      const desc = offer.description ? this.escapeHTML(offer.description) : '';
      const label = `${offer.label || 'Same-Day Delivery'} — ${offer.display_date || 'Today'}${price ? ` (${price})` : ''}`;
      return `
        <button type="button" class="fulfillment-delivery-drawer__store-block fulfillment-delivery-drawer__select fulfillment-delivery-drawer__same-day" data-select-delivery data-delivery-type="same_day" data-label="${this.escapeHTML(label)}">
          <span>${title}${price ? ` &middot; ${price}` : ''}</span>
          <p class="fulfillment-delivery-drawer__success">${when}${desc ? ` &middot; ${desc}` : ''}</p>
          <span class="fulfillment-delivery-drawer__note">Max ${max} lbs</span>
          <span class="fulfillment-delivery-drawer__select-cta">Choose Same-Day Delivery</span>
        </button>
      `;
    }

    renderFedexDeliveryBlock(fedex) {
      const services = Array.isArray(fedex.services) ? fedex.services : [];

      if (!fedex.available || services.length === 0) {
        return `
          <div class="fulfillment-delivery-drawer__store-block">
            <span>Shipping options</span>
            <p>Shipping options will be confirmed at checkout.</p>
          </div>
        `;
      }

      const best = services[0] || {};
      const dateText = best.display_text || 'Delivery date confirmed at checkout';
      return `
        <div class="fulfillment-delivery-drawer__store-block">
          <p><strong>This item will ship by FedEx Ground Shipping</strong></p>
          <p class="fulfillment-delivery-drawer__success">${this.escapeHTML(dateText)}</p>
        </div>
      `;
    }

    formatMoneyFromCents(cents) {
      const value = Number(cents);
      if (!Number.isFinite(value)) return '$--';
      return `$${(value / 100).toFixed(2)}`;
    }

    getSortedStoreHandles(locations, options = {}) {
      const sortEarliest = options.sortEarliest !== false;

      return this.getAllStoreHandles().sort((a, b) => {
        const aInStock = locations[a] === true ? 1 : 0;
        const bInStock = locations[b] === true ? 1 : 0;
        const aMeta = this.getStoreMeta(a);
        const bMeta = this.getStoreMeta(b);
        const aDistance = Number.isFinite(aMeta.distanceValue) ? aMeta.distanceValue : Number.MAX_SAFE_INTEGER;
        const bDistance = Number.isFinite(bMeta.distanceValue) ? bMeta.distanceValue : Number.MAX_SAFE_INTEGER;

        if (sortEarliest && aInStock !== bInStock) return bInStock - aInStock;
        if (aDistance !== bDistance) return aDistance - bDistance;

        return aMeta.order - bMeta.order;
      });
    }

    getAllStoreHandles() {
      const cardHandles = (this.storeSelector?.cards || []).map(card => card.dataset.handle).filter(Boolean);
      const listHandles = this.storeSelector?.storesList ? Object.keys(this.storeSelector.storesList) : [];
      return [...new Set([...cardHandles, ...listHandles])];
    }

    // The in-stock stores closest to the home store, by geographic distance from
    // the home store's coordinates (works without the shopper's location). Returns
    // up to `limit` entries as [{handle, dist}], nearest first.
    getNearestInStockHandles(inStockHandles, homeHandle, limit = 2) {
      if (!inStockHandles || !inStockHandles.length || !homeHandle) return [];
      const inStock = new Set(inStockHandles);
      return this.getStoreDistanceRanking(homeHandle)
        .filter(r => r.handle !== homeHandle && inStock.has(r.handle))
        .slice(0, limit);
    }

    // Memoized full ranking of other stores by distance from `homeHandle`. Computed
    // once per home store (cached on the class) from the configured store coordinates —
    // a lookup table built on first use, with no separate regeneration step.
    getStoreDistanceRanking(homeHandle) {
      const cache = PickupAvailabilityCompact.distanceRankCache;
      if (cache.has(homeHandle)) return cache.get(homeHandle);

      const homeCard = this.getStoreCard(homeHandle);
      const homeLat = parseFloat(homeCard?.dataset.lat);
      const homeLng = parseFloat(homeCard?.dataset.lng);
      const haveHome = Number.isFinite(homeLat) && Number.isFinite(homeLng);

      const ranked = this.getAllStoreHandles()
        .filter(handle => handle !== homeHandle)
        .map(handle => {
          const card = this.getStoreCard(handle);
          const lat = parseFloat(card?.dataset.lat);
          const lng = parseFloat(card?.dataset.lng);
          const dist = haveHome && Number.isFinite(lat) && Number.isFinite(lng)
            ? this.getDistanceMiles(homeLat, homeLng, lat, lng)
            : Number.POSITIVE_INFINITY;
          return { handle, dist, order: Number(card?.dataset.order || 0) };
        })
        .sort((a, b) => (a.dist !== b.dist ? a.dist - b.dist : a.order - b.order));

      cache.set(homeHandle, ranked);
      return ranked;
    }

    getClosestInStockHandle(inStockHandles) {
      if (!inStockHandles.length) return null;

      const selectedStore = localStorage.getItem('selected-store');
      const withMeta = inStockHandles.map(handle => {
        const meta = this.getStoreMeta(handle);
        return {
          handle,
          distanceValue: meta.distanceValue,
          order: meta.order
        };
      });
      const hasDistance = withMeta.some(store => Number.isFinite(store.distanceValue));

      if (hasDistance) {
        return withMeta.sort((a, b) => {
          const aDistance = Number.isFinite(a.distanceValue) ? a.distanceValue : Number.MAX_SAFE_INTEGER;
          const bDistance = Number.isFinite(b.distanceValue) ? b.distanceValue : Number.MAX_SAFE_INTEGER;
          return aDistance - bDistance;
        })[0].handle;
      }

      if (selectedStore && inStockHandles.includes(selectedStore)) return selectedStore;

      return withMeta.sort((a, b) => a.order - b.order)[0].handle;
    }

    getStoreMeta(handle) {
      const card = this.getStoreCard(handle);
      const distanceValue = card ? Number(card.dataset.distanceValue) : NaN;
      const addressEl = card?.querySelector('.store-selector-item__address');
      const address = addressEl ? this.formatStoreAddressElement(addressEl) : '';
      const statusText = card?.querySelector('[data-status-text]')?.textContent.trim().replace(/\s+/g, ' ');

      return {
        handle,
        name: this.storeSelector?.storesList?.[handle] || card?.dataset.title || this.formatHandle(handle),
        address: address || '',
        statusText: statusText || '',
        distanceValue,
        distance: Number.isFinite(distanceValue) ? this.formatDistance(distanceValue) : '',
        order: Number(card?.dataset.order || 0)
      };
    }

    formatStoreAddressElement(addressEl) {
      const rows = [];
      const blocks = Array.from(addressEl.querySelectorAll('p'));
      const sources = blocks.length ? blocks : [addressEl];

      sources.forEach(source => {
        const clone = source.cloneNode(true);
        clone.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
        clone.textContent
          .split('\n')
          .map(row => row.trim().replace(/\s+/g, ' '))
          .filter(Boolean)
          .forEach(row => rows.push(row));
      });

      return rows.join(', ');
    }

    getStoreCard(handle) {
      if (!handle) return null;
      const cards = this.storeSelector?.cards || Array.from(document.querySelectorAll('store-selector[data-main-selector] .store-selector-item'));
      return cards.find(card => card.dataset.handle === handle) || null;
    }

    bindFulfillmentPanelActions() {
      this.querySelectorAll('[data-select-fulfillment]').forEach(button => {
        button.addEventListener('click', () => {
          const targetMethod = button.dataset.selectFulfillment === 'delivery' ? 'delivery' : 'pickup';
          if (this._fulfillmentMethod === targetMethod) {
            if (targetMethod === 'delivery') {
              this.openDeliveryDrawer();
            } else {
              this.openStoreAvailabilityDrawer();
            }
            return;
          }
          this._fulfillmentMethod = targetMethod;
          this.renderFulfillmentPanel(this._stockData);
        });
      });

      this.querySelectorAll('[data-open-store-drawer]').forEach(button => {
        button.addEventListener('click', () => {
          this.openStoreAvailabilityDrawer();
        });
      });

      this.querySelectorAll('[data-close-store-drawer]').forEach(button => {
        button.addEventListener('click', () => {
          this.closeStoreAvailabilityDrawer();
        });
      });

      this.querySelectorAll('[data-store-drawer-search]').forEach(form => {
        form.addEventListener('submit', event => {
          event.preventDefault();
          this.searchFulfillmentDrawer(form.querySelector('[data-store-drawer-input]')?.value.trim());
        });
      });

      this.querySelectorAll('[data-store-drawer-input]').forEach(input => {
        input.addEventListener('input', () => {
          this._drawerLocationQuery = input.value;
          this.queueFulfillmentDrawerSuggestions(input.value.trim());
        });
        input.addEventListener('focus', () => {
          this.queueFulfillmentDrawerSuggestions(input.value.trim());
        });
      });

      this.querySelectorAll('[data-store-drawer-locate]').forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          this.locateFulfillmentDrawer();
        });
      });

      this.querySelectorAll('[data-store-drawer-sort-toggle]').forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          this._storeDrawerSortEarliest = !(this._storeDrawerSortEarliest !== false);
          this.updateFulfillmentDrawerSort();
        });
      });

      this.querySelectorAll('[data-open-delivery-drawer]').forEach(button => {
        button.addEventListener('click', () => {
          this.openDeliveryDrawer();
        });
      });

      this.querySelectorAll('[data-close-delivery-drawer]').forEach(button => {
        button.addEventListener('click', () => {
          this.closeDeliveryDrawer();
        });
      });

      this.querySelectorAll('[data-delivery-address-form]').forEach(form => {
        form.addEventListener('submit', event => {
          event.preventDefault();
          this.searchDeliveryAddress(form.querySelector('[data-delivery-address-input]')?.value.trim());
        });
      });

      this.querySelectorAll('[data-delivery-address-input]').forEach(input => {
        input.addEventListener('input', () => {
          this._deliveryAddress = input.value;
          if (input.dataset.googlePlacesActive === 'true') {
            this._deliverySuggestions = [];
            this.renderDeliveryAddressSuggestions();
          } else {
            this.queueDeliveryAddressSuggestions(input.value.trim());
          }
        });
        input.addEventListener('focus', () => {
          if (input.dataset.googlePlacesActive === 'true') return;
          this.queueDeliveryAddressSuggestions(input.value.trim());
        });
        this.attachDeliveryAddressAutocomplete(input);
      });

      this.querySelectorAll('[data-clear-delivery-address]').forEach(button => {
        button.addEventListener('click', () => {
          this._deliveryAddress = '';
          this._deliveryStatus = '';
          this._deliveryResult = null;
          this._deliverySuggestions = [];
          this.refreshDeliveryDrawer();
        });
      });

      this.querySelectorAll('[data-pickup-choice="nearby"]').forEach(choice => {
        const handle = choice.dataset.handle;
        const radio = choice.querySelector('input[type="radio"]');
        radio?.addEventListener('change', () => {
          if (!radio.checked || !handle) return;
          this._fulfillmentMethod = 'pickup'; // choosing a pickup store implies pickup
          if (this.storeSelector && typeof this.storeSelector._setAceSelectedStore === 'function') {
            this.storeSelector._setAceSelectedStore(handle);
          } else {
            localStorage.setItem('selected-store', handle);
            this.storeSelector?.dispatchEvent(new CustomEvent('storechanged'));
            this.renderWithStock();
          }
        });
      });

      this.bindQuantitySync();
      this.bindDeliverySelectActions(this);
      this.bindFulfillmentDrawerStoreActions(this);
    }

    // Clicking a delivery result selects Delivery as the fulfillment method.
    bindDeliverySelectActions(root = this) {
      root.querySelectorAll('[data-select-delivery]').forEach(button => {
        if (button.dataset.deliverySelectBound === 'true') return;
        button.dataset.deliverySelectBound = 'true';
        button.addEventListener('click', () => {
          this._fulfillmentMethod = 'delivery';
          this._deliveryChoiceType = button.dataset.deliveryType === 'same_day' ? 'same_day' : 'standard';
          this._deliveryChoiceLabel = button.dataset.label || 'Delivery';
          // Remember the specific method + date so the product-page card shows what was
          // actually chosen (e.g. "Local delivery"), not a generic "Delivery".
          this._deliveryChoiceMethod = button.dataset.method || '';
          this._deliveryChoiceDate = button.dataset.date || '';
          this.closeDeliveryDrawer({ remove: true });
          if (this.isFulfillmentPanel()) this.renderFulfillmentPanel(this._stockData);
        });
      });
    }

    bindQuantitySync() {
      if (this._qtySyncBound) return;
      const form = this.getProductForm();
      const qtyInput = form?.querySelector('[name="quantity"]') || document.querySelector('[name="quantity"]');
      if (!qtyInput) return;
      this._qtySyncBound = true;
      qtyInput.addEventListener('change', () => {
        if (this.isFulfillmentPanel() && this._stockData) {
          this.renderFulfillmentPanel(this._stockData);
        }
      });
    }

    searchFulfillmentDrawer(query) {
      if (!query) {
        this._drawerLocationStatus = 'Enter a ZIP code or city to sort nearby stores.';
        this._drawerLocationSuggestions = [];
        this.refreshFulfillmentDrawer();
        return;
      }

      if (!this.storeSelector || typeof this.storeSelector._ensureGoogleMaps !== 'function') {
        this._drawerLocationStatus = 'Location search is not available yet.';
        this.refreshFulfillmentDrawer();
        return;
      }

      this._drawerLocationQuery = query;
      this._drawerLocationStatus = 'Searching nearby stores...';
      this._drawerLocationSuggestions = [];
      this.refreshFulfillmentDrawer();

      this.resolveGoogleAddressSuggestion({ address: query }, { requireStreet: false })
        .then(point => {
          this.applyFulfillmentDrawerPoint(point);
          this._drawerLocationStatus = `Stores closest to ${query}. Earliest available stores are listed first.`;
          this.refreshFulfillmentDrawer();
        })
        .catch(() => {
          this._drawerLocationStatus = 'We could not find that location. Try a ZIP code or city and state.';
          this.refreshFulfillmentDrawer();
        });
    }

    queueFulfillmentDrawerSuggestions(query) {
      clearTimeout(this._drawerLocationSuggestTimer);

      if (!query || query.length < 4) {
        this._drawerLocationSuggestions = [];
        this.renderFulfillmentDrawerSuggestions();
        return;
      }

      this._drawerLocationSuggestTimer = setTimeout(() => {
        this.fetchFulfillmentDrawerSuggestions(query);
      }, 300);
    }

    fetchFulfillmentDrawerSuggestions(query) {
      this.getGoogleAddressSuggestions(query, { requireStreet: false })
        .then(suggestions => {
          if ((this._drawerLocationQuery || '').trim() !== query) return;
          this._drawerLocationSuggestions = suggestions;
          this.renderFulfillmentDrawerSuggestions();
        })
        .catch(() => {
          this._drawerLocationSuggestions = [];
          this.renderFulfillmentDrawerSuggestions();
        });
    }

    renderFulfillmentDrawerSuggestions() {
      const drawer = this._activeStoreDrawer || document.querySelector('.fulfillment-store-drawer:not([hidden])');
      const suggestionsEl = drawer?.querySelector('[data-store-drawer-suggestions]');
      if (!suggestionsEl) return;

      const suggestions = this._drawerLocationSuggestions || [];
      suggestionsEl.hidden = suggestions.length === 0;
      suggestionsEl.innerHTML = suggestions.map((suggestion, index) => `
        <button type="button" data-store-drawer-suggestion-index="${index}">
          <span aria-hidden="true"></span>
          ${this.escapeHTML(suggestion.address)}
        </button>
      `).join('');

      suggestionsEl.querySelectorAll('[data-store-drawer-suggestion-index]').forEach(button => {
        button.addEventListener('click', () => {
          const suggestion = suggestions[Number(button.dataset.storeDrawerSuggestionIndex)];
          if (!suggestion) return;
          this._drawerLocationQuery = suggestion.address;
          this._drawerLocationSuggestions = [];
          this._drawerLocationStatus = 'Searching nearby stores...';
          this.refreshFulfillmentDrawer();

          this.resolveGoogleAddressSuggestion(suggestion, { requireStreet: false })
            .then(point => {
              this._drawerLocationQuery = point.address || suggestion.address;
              this.applyFulfillmentDrawerPoint(point);
              this._drawerLocationStatus = `Stores closest to ${this._drawerLocationQuery}. Earliest available stores are listed first.`;
              this.refreshFulfillmentDrawer();
            })
            .catch(() => {
              this._drawerLocationStatus = 'We could not find that location. Try a ZIP code or city and state.';
              this.refreshFulfillmentDrawer();
            });
        });
      });
    }

    locateFulfillmentDrawer() {
      if (!navigator.geolocation) {
        this._drawerLocationStatus = 'Current location is not available in this browser.';
        this.refreshFulfillmentDrawer();
        return;
      }

      this._drawerLocationStatus = 'Finding your current location...';
      this.refreshFulfillmentDrawer();

      navigator.geolocation.getCurrentPosition(
        position => {
          this._drawerLocationQuery = '';
          this.applyFulfillmentDrawerPoint({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          this._drawerLocationStatus = 'Stores closest to your current location. Earliest available stores are listed first.';
          this.refreshFulfillmentDrawer();
        },
        () => {
          this._drawerLocationStatus = 'Location permission was blocked. Enter a ZIP code instead.';
          this.refreshFulfillmentDrawer();
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    applyFulfillmentDrawerPoint(point) {
      this.getAllStoreHandles().forEach(handle => {
        const card = this.getStoreCard(handle);
        if (!card) return;

        const lat = parseFloat(card.dataset.lat);
        const lng = parseFloat(card.dataset.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const distance = this.getDistanceMiles(point.lat, point.lng, lat, lng);
        card.dataset.distanceValue = distance.toFixed(4);
      });
    }

    refreshFulfillmentDrawer() {
      this._activeStoreDrawer?.remove();
      this._activeStoreDrawer = null;
      this.renderFulfillmentPanel(this._stockData, this._sectionHTML);
      this.openStoreAvailabilityDrawer();
    }

    openDeliveryDrawer() {
      const drawer = this.querySelector('[data-delivery-drawer]');
      if (!drawer) return;
      this._activeDeliveryDrawer = drawer;
      document.body.appendChild(drawer);
      document.body.classList.add('fulfillment-store-drawer-open');
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => drawer.classList.add('is-open'));
    }

    closeDeliveryDrawer(options = {}) {
      const owner = this.dataset.id || 'product';
      const drawer = this._activeDeliveryDrawer || Array.from(document.querySelectorAll('.fulfillment-delivery-drawer[data-owner]')).find(elm => elm.dataset.owner === owner);
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('fulfillment-store-drawer-open');
      setTimeout(() => {
        drawer.hidden = true;
        if (options.remove) {
          drawer.remove();
        } else if (this.isConnected && !this.contains(drawer)) {
          this.appendChild(drawer);
        }
        if (this._activeDeliveryDrawer === drawer) {
          this._activeDeliveryDrawer = null;
        }
      }, 180);
    }

    searchDeliveryAddress(query) {
      if (!query) {
        this._deliveryStatus = 'Enter a delivery address to check local delivery.';
        this.refreshDeliveryDrawer();
        return;
      }

      if (!this.storeSelector || (typeof this.storeSelector._ensureGoogleMaps !== 'function' && typeof this.storeSelector._geocodeAceLocation !== 'function')) {
        this._deliveryStatus = 'Delivery address search is not available yet.';
        this.refreshDeliveryDrawer();
        return;
      }

      this._deliveryAddress = query;
      this._deliveryStatus = 'Checking delivery address...';
      this.refreshDeliveryDrawer();

      this.geocodeDeliveryAddress(query)
        .then(point => this.applyDeliveryAddressPoint(point))
        .catch(() => {
          this._deliveryStatus = 'We could not find that address or ZIP code. Try the full street, city, state, and ZIP.';
          this.refreshDeliveryDrawer();
        });
    }

    queueDeliveryAddressSuggestions(query) {
      clearTimeout(this._deliverySuggestTimer);

      if (!query || query.length < 4) {
        this._deliverySuggestions = [];
        this.renderDeliveryAddressSuggestions();
        return;
      }

      this._deliverySuggestTimer = setTimeout(() => {
        this.fetchDeliveryAddressSuggestions(query);
      }, 350);
    }

    renderDrawerNearbyStoreRows({ nearbyHandles, locations, hasStockData }) {
      return nearbyHandles.map(handle => {
        const meta = this.getStoreMeta(handle);
        const availability = this.getStoreAvailability(handle, locations, hasStockData);
        return this.renderDrawerStoreRow(handle, meta, availability, false);
      }).join('');
    }

    updateFulfillmentDrawerSort() {
      const drawer = this._activeStoreDrawer || document.querySelector('.fulfillment-store-drawer:not([hidden])');
      if (!drawer) {
        this.refreshFulfillmentDrawer();
        return;
      }

      const selectedStore = localStorage.getItem('selected-store');
      const locations = this._stockData?.locations || {};
      const hasStockData = !!(this._stockData && this._stockData.locations);
      const sortEarliest = this._storeDrawerSortEarliest !== false;
      const nearbyHandles = this.getSortedStoreHandles(locations, { sortEarliest }).filter(handle => handle !== selectedStore);
      const storesEl = drawer.querySelector('.fulfillment-store-drawer__stores');
      const toggle = drawer.querySelector('[data-store-drawer-sort-toggle]');

      if (storesEl) {
        storesEl.innerHTML = this.renderDrawerNearbyStoreRows({ nearbyHandles, locations, hasStockData });
      }

      if (toggle) {
        toggle.classList.toggle('is-on', sortEarliest);
        toggle.setAttribute('aria-pressed', sortEarliest ? 'true' : 'false');
      }

      this.bindFulfillmentDrawerStoreActions(drawer);
    }

    bindFulfillmentDrawerStoreActions(root = this) {
      root.querySelectorAll('[data-set-fulfillment-store]').forEach(button => {
        if (button.dataset.fulfillmentStoreBound === 'true') return;
        button.dataset.fulfillmentStoreBound = 'true';
        button.addEventListener('click', () => {
          const handle = button.dataset.setFulfillmentStore;
          if (!handle) return;
          this._fulfillmentMethod = 'pickup'; // choosing a pickup store implies pickup
          this.closeStoreAvailabilityDrawer({ remove: true });
          this.closeDeliveryDrawer({ remove: true });

          if (this.storeSelector && typeof this.storeSelector._setAceSelectedStore === 'function') {
            this.storeSelector._setAceSelectedStore(handle);
          } else {
            localStorage.setItem('selected-store', handle);
            this.storeSelector?.dispatchEvent(new CustomEvent('storechanged'));
            this.renderWithStock();
          }
        });
      });
    }

    fetchDeliveryAddressSuggestions(query) {
      this.getGoogleAddressSuggestions(query, { requireStreet: false, predictionTypes: ['geocode'] })
        .then(suggestions => {
          if ((this._deliveryAddress || '').trim() !== query) return;
          this._deliverySuggestions = suggestions;
          this.renderDeliveryAddressSuggestions();
        })
        .catch(() => {
          this._deliverySuggestions = [];
          this.renderDeliveryAddressSuggestions();
        });
    }

    renderDeliveryAddressSuggestions() {
      const drawer = this._activeDeliveryDrawer || document.querySelector('.fulfillment-delivery-drawer:not([hidden])');
      const suggestionsEl = drawer?.querySelector('[data-delivery-address-suggestions]');
      if (!suggestionsEl) return;

      const suggestions = this._deliverySuggestions || [];
      suggestionsEl.hidden = suggestions.length === 0;
      suggestionsEl.innerHTML = suggestions.map((suggestion, index) => `
        <button type="button" data-delivery-suggestion-index="${index}">
          <span aria-hidden="true"></span>
          ${this.escapeHTML(suggestion.address)}
        </button>
      `).join('');

      suggestionsEl.querySelectorAll('[data-delivery-suggestion-index]').forEach(button => {
        button.addEventListener('click', () => {
          const suggestion = suggestions[Number(button.dataset.deliverySuggestionIndex)];
          if (!suggestion) return;
          this._deliveryAddress = suggestion.address;
          this._deliverySuggestions = [];
          this._deliveryStatus = 'Checking delivery address...';
          this.updateDeliveryDrawerInPlace();

          this.resolveGoogleAddressSuggestion(suggestion, { requireStreet: false })
            .then(point => this.applyDeliveryAddressPoint(point))
            .catch(() => {
              this._deliveryStatus = 'We could not find that address or ZIP code. Try the full street, city, state, and ZIP.';
              this.updateDeliveryDrawerInPlace();
            });
        });
      });
    }

    geocodeDeliveryAddress(query) {
      if (this.storeSelector && typeof this.storeSelector._ensureGoogleMaps === 'function') {
        return this.storeSelector._ensureGoogleMaps()
          .then(() => new Promise((resolve, reject) => {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({
              address: query,
              componentRestrictions: { country: 'US' }
            }, (results, status) => {
              if (status !== 'OK' || !results || !results.length) {
                reject(status);
                return;
              }

              const result = results.find(item => this.isUsableGoogleAddressResult(item, { requireStreet: false }));
              if (!result) {
                reject(status);
                return;
              }

              resolve(this.parseGoogleAddressResult(result));
            });
          }))
          .catch(() => this.geocodeWithRooSuggestions(query))
          .catch(() => this.geocodeWithStoreSelector(query));
      }

      return this.geocodeWithRooSuggestions(query)
        .catch(() => this.geocodeWithStoreSelector(query));
    }

    getGoogleAddressSuggestions(query, options = {}) {
      if (!query) return Promise.resolve([]);

      return this.fetchRooAddressSuggestions(query)
        .catch(() => this.fetchGooglePlacePredictions(query, options))
        .catch(() => this.fetchGoogleGeocodeSuggestions(query, options));
    }

    fetchRooAddressSuggestions(query) {
      if (!query || query.length < 3) return Promise.resolve([]);

      return fetch(`/apps/roo/address-suggestions?query=${encodeURIComponent(query)}`)
        .then(response => {
          if (!response.ok) throw new Error('Address suggestions unavailable');
          return response.json();
        })
        .then(data => {
          const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
          return suggestions
            .filter(suggestion => suggestion?.address && Number.isFinite(Number(suggestion.lat)) && Number.isFinite(Number(suggestion.lng)))
            .slice(0, 5);
        });
    }

    attachDeliveryAddressAutocomplete(input) {
      if (!input || input.dataset.googlePlacesBound === 'true') return;
      input.dataset.googlePlacesBound = 'true';

      this.ensureGooglePlaces()
        .then(() => {
          if (typeof google === 'undefined' || !google.maps?.places?.Autocomplete) return;
          const autocomplete = new google.maps.places.Autocomplete(input, {
            componentRestrictions: { country: 'us' },
            fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
            types: ['geocode']
          });
          input.dataset.googlePlacesActive = 'true';
          this._deliverySuggestions = [];
          this.renderDeliveryAddressSuggestions();
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place?.geometry?.location) return;
            const point = this.parseGoogleAddressResult(place);
            this._deliveryAddress = point.address || input.value;
            this._deliverySuggestions = [];
            this.updateDeliveryDrawerInPlace();
            this.applyDeliveryAddressPoint(point);
          });
        })
        .catch(() => {});
    }

    geocodeWithRooSuggestions(query) {
      return this.fetchRooAddressSuggestions(query)
        .then(suggestions => {
          if (!suggestions[0]) throw new Error('No Roo address suggestion');
          return suggestions[0];
        });
    }

    geocodeWithStoreSelector(query) {
      if (!this.storeSelector || typeof this.storeSelector._geocodeAceLocation !== 'function') {
        return Promise.reject();
      }

      return this.storeSelector._geocodeAceLocation(query)
        .then(point => ({
          address: query,
          lat: point.lat,
          lng: point.lng
        }));
    }

    fetchGooglePlacePredictions(query, options = {}) {
      return this.ensureGooglePlaces()
        .then(() => new Promise((resolve, reject) => {
          if (typeof google === 'undefined' || !google.maps?.places?.AutocompleteService) {
            reject();
            return;
          }

          const service = new google.maps.places.AutocompleteService();
          const okStatus = google.maps.places.PlacesServiceStatus?.OK || 'OK';
          const request = {
            input: query,
            componentRestrictions: { country: 'us' },
            types: options.predictionTypes || (options.requireStreet ? ['address'] : ['geocode'])
          };

          service.getPlacePredictions(request, (predictions, status) => {
            if (status !== okStatus || !predictions?.length) {
              reject(status);
              return;
            }

            const suggestions = predictions
              .filter(prediction => !this.isBroadGooglePrediction(prediction))
              .slice(0, 5)
              .map(prediction => ({
                address: prediction.description,
                placeId: prediction.place_id
              }));

            suggestions.length ? resolve(suggestions) : reject(status);
          });
        }));
    }

    fetchGoogleGeocodeSuggestions(query, options = {}) {
      if (!this.storeSelector || typeof this.storeSelector._ensureGoogleMaps !== 'function') return Promise.reject();

      return this.storeSelector._ensureGoogleMaps()
        .then(() => new Promise((resolve, reject) => {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({
            address: query,
            componentRestrictions: { country: 'US' }
          }, (results, status) => {
            if (status !== 'OK' || !results?.length) {
              reject(status);
              return;
            }

            const suggestions = results
              .filter(result => this.isUsableGoogleAddressResult(result, options))
              .slice(0, 5)
              .map(result => this.parseGoogleAddressResult(result));

            suggestions.length ? resolve(suggestions) : reject(status);
          });
        }));
    }

    resolveGoogleAddressSuggestion(suggestion = {}, options = {}) {
      if (Number.isFinite(Number(suggestion.lat)) && Number.isFinite(Number(suggestion.lng))) {
        return Promise.resolve(suggestion);
      }

      if (!this.storeSelector || typeof this.storeSelector._ensureGoogleMaps !== 'function') return Promise.reject();

      return this.storeSelector._ensureGoogleMaps()
        .then(() => new Promise((resolve, reject) => {
          const geocoder = new google.maps.Geocoder();
          const request = suggestion.placeId
            ? { placeId: suggestion.placeId }
            : { address: suggestion.address, componentRestrictions: { country: 'US' } };

          geocoder.geocode(request, (results, status) => {
            if (status !== 'OK' || !results?.length) {
              reject(status);
              return;
            }

            const result = results.find(item => this.isUsableGoogleAddressResult(item, options));
            if (!result) {
              reject(status);
              return;
            }

            resolve(this.parseGoogleAddressResult(result));
          });
        }));
    }

    ensureGooglePlaces() {
      if (!this.storeSelector || typeof this.storeSelector._ensureGoogleMaps !== 'function') return Promise.reject();

      return this.storeSelector._ensureGoogleMaps()
        .then(() => {
          if (typeof google === 'undefined' || !google.maps) return Promise.reject();
          if (google.maps.places?.AutocompleteService) return Promise.resolve();
          if (typeof google.maps.importLibrary === 'function') {
            return google.maps.importLibrary('places');
          }
          return Promise.reject();
        });
    }

    isBroadGooglePrediction(prediction = {}) {
      const terms = prediction.terms || [];
      const types = prediction.types || [];
      return terms.length <= 1 || types.includes('country');
    }

    isUsableGoogleAddressResult(result = {}, options = {}) {
      const types = result.types || [];
      if (types.includes('country')) return false;

      const parts = this.getGoogleAddressParts(result);
      const hasStreet = !!(parts.street_number && parts.route);
      if (options.requireStreet && !hasStreet) return false;

      return hasStreet
        || !!parts.postal_code
        || !!parts.locality
        || !!parts.sublocality
        || !!parts.administrative_area_level_2
        || !!parts.administrative_area_level_1;
    }

    parseGoogleAddressResult(result) {
      const parts = this.getGoogleAddressParts(result);
      const streetNumber = parts.street_number?.long || '';
      const route = parts.route?.long || '';
      const postalCode = [
        parts.postal_code?.short || '',
        parts.postal_code_suffix?.short || ''
      ].filter(Boolean).join('-');
      const address = result.formatted_address || '';
      const location = result.geometry?.location;
      const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
      const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;

      return {
        address,
        address1: [streetNumber, route].filter(Boolean).join(' ') || '',
        city: parts.locality?.long || parts.sublocality?.long || parts.administrative_area_level_2?.long || '',
        province: parts.administrative_area_level_1?.short || '',
        postal_code: postalCode,
        country: parts.country?.short || 'US',
        lat: Number.isFinite(Number(lat)) ? Number(lat) : null,
        lng: Number.isFinite(Number(lng)) ? Number(lng) : null
      };
    }

    getGoogleAddressParts(result) {
      const parts = {};
      (result.address_components || []).forEach(component => {
        (component.types || []).forEach(type => {
          parts[type] = {
            long: component.long_name,
            short: component.short_name
          };
        });
      });

      return parts;
    }

    applyDeliveryAddressPoint(point = {}) {
      if (Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng))) {
        this.applyFulfillmentDrawerPoint(point);
      }

      this._deliveryAddress = point.address || this._deliveryAddress;
      const savedZip = (point.postal_code || this.extractZip(this._deliveryAddress) || '').slice(0, 5);
      if (savedZip) {
        try {
          localStorage.setItem('hatchet_delivery_zip', savedZip);
          localStorage.setItem('hatchet_delivery_point', JSON.stringify({
            address: this._deliveryAddress || '',
            address1: point.address1 || '',
            city: point.city || '',
            province: point.province || '',
            postal_code: savedZip,
            country: point.country || 'US',
            lat: point.lat ?? null,
            lng: point.lng ?? null
          }));
        } catch (_) {}
      }
      this._deliveryStatus = 'Checking delivery options...';
      this._deliveryResult = null;
      this.refreshDeliveryDrawer();

      const params = this.buildDeliveryOptionsParams(point);
      if (!params) {
        this._deliveryStatus = 'Enter a street address, city, state, or ZIP code.';
        this.refreshDeliveryDrawer();
        return;
      }

      const requestId = Date.now();
      this._deliveryRequestId = requestId;

      fetch(`/apps/roo/delivery-options?${params.toString()}`)
        .then(response => {
          if (!response.ok) throw new Error('Delivery options unavailable');
          return response.json();
        })
        .then(data => {
          if (this._deliveryRequestId !== requestId) return;
          this._deliveryStatus = '';
          this._deliveryResult = data;
          this.refreshDeliveryDrawer();
        })
        .catch(() => {
          if (this._deliveryRequestId !== requestId) return;
          this._deliveryStatus = "We couldn't calculate FedEx delivery right now. Shipping options will be confirmed at checkout.";
          this._deliveryResult = null;
          this.refreshDeliveryDrawer();
        });
    }

    buildDeliveryOptionsParams(point = {}) {
      const variantId = this.dataset.variantId;
      if (!variantId) return null;

      const fallbackAddress = point.address || this._deliveryAddress || '';
      const postalCode = point.postal_code || (fallbackAddress.match(/\b\d{5}(?:-\d{4})?\b/) || [])[0] || '';
      if (!postalCode) return null;

      const params = new URLSearchParams();
      params.set('variant_id', variantId);
      params.set('quantity', this.getSelectedQuantity());
      params.set('selected_store', localStorage.getItem('selected-store') || '');
      params.set('weight_grams', this.dataset.variantWeightGrams || '');
      params.set('address1', point.address1 || '');
      params.set('city', point.city || '');
      params.set('province', point.province || '');
      params.set('postal_code', postalCode);
      params.set('country', point.country || 'US');

      if (Number.isFinite(Number(point.lat))) params.set('lat', point.lat);
      if (Number.isFinite(Number(point.lng))) params.set('lng', point.lng);

      return params;
    }

    getSelectedQuantity() {
      const form = this.closest('form') || document.querySelector('form[action*="/cart/add"]');
      const quantityInput = form?.querySelector('[name="quantity"]') || document.querySelector('[name="quantity"]');
      const quantity = parseInt(quantityInput?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? String(quantity) : '1';
    }

    // The restriction notice lives in the product section (main-product.liquid), outside this
    // component, and is rendered HIDDEN: the page is cached and Liquid cannot know the buyer's
    // address, so the show/hide decision belongs here. Unhide ONLY when the backend positively
    // resolved the entered destination to a restricted state — restricted_reason 'state_unknown'
    // means we could not determine where they are, which must not be presented to a New York
    // shopper as "cannot be shipped to KY".
    syncRestrictedNotice() {
      const scope = this.closest('.shopify-section') || document;
      const note = scope.querySelector('[data-restricted-states-note]');
      if (!note) return;
      const result = this._deliveryResult;
      note.hidden = !(result && result.restricted_state === true && result.restricted_reason === 'state');
    }

    refreshDeliveryDrawer() {
      this.syncRestrictedNotice();
      if (this.updateDeliveryDrawerInPlace()) return;

      this._activeDeliveryDrawer?.remove();
      this._activeDeliveryDrawer = null;
      this.renderFulfillmentPanel(this._stockData, this._sectionHTML);
      this.openDeliveryDrawer();
    }

    updateDeliveryDrawerInPlace() {
      const owner = this.dataset.id || 'product';
      const drawer = this._activeDeliveryDrawer || Array.from(document.querySelectorAll('.fulfillment-delivery-drawer[data-owner]')).find(elm => elm.dataset.owner === owner && !elm.hidden);
      if (!drawer || drawer.hidden || !drawer.isConnected) return false;

      this._activeDeliveryDrawer = drawer;

      const input = drawer.querySelector('[data-delivery-address-input]');
      if (input && document.activeElement !== input && input.value !== (this._deliveryAddress || '')) {
        input.value = this._deliveryAddress || '';
      }

      const statusEl = drawer.querySelector('[data-delivery-address-status]');
      if (statusEl) {
        statusEl.textContent = this._deliveryStatus || '';
      }

      let resultRegion = drawer.querySelector('[data-delivery-result-region]');
      if (!resultRegion) {
        resultRegion = document.createElement('div');
        resultRegion.setAttribute('data-delivery-result-region', '');
        drawer.querySelector('[data-delivery-address-form]')?.insertAdjacentElement('afterend', resultRegion);
      }
      resultRegion.innerHTML = this._deliveryResult ? this.renderDeliveryResult(this._deliveryResult) : '';

      this.renderDeliveryAddressSuggestions();
      this.bindDeliverySelectActions(drawer);
      this.updateDeliveryCard();
      return true;
    }

    openStoreAvailabilityDrawer() {
      const drawer = this.querySelector('[data-store-availability-drawer]');
      if (!drawer) return;
      this._activeStoreDrawer = drawer;
      document.body.appendChild(drawer);
      document.body.classList.add('fulfillment-store-drawer-open');
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => drawer.classList.add('is-open'));
    }

    closeStoreAvailabilityDrawer(options = {}) {
      const owner = this.dataset.id || 'product';
      const drawer = this._activeStoreDrawer || Array.from(document.querySelectorAll('.fulfillment-store-drawer[data-owner]')).find(elm => elm.dataset.owner === owner);
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('fulfillment-store-drawer-open');
      setTimeout(() => {
        drawer.hidden = true;
        if (options.remove) {
          drawer.remove();
        } else if (this.isConnected && !this.contains(drawer)) {
          this.appendChild(drawer);
        }
        if (this._activeStoreDrawer === drawer) {
          this._activeStoreDrawer = null;
        }
      }, 180);
    }

    getDistanceMiles(lat1, lng1, lat2, lng2) {
      const toRad = value => value * Math.PI / 180;
      const earthRadius = 3958.8;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
      return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    // Transfer-date text for the ships-to-store branches. The date is
    // computed by the backend (per-store lead time, GET /apps/roo/stock-locations
    // → leadTimes[handle]) — never in the browser. We read the ISO readyDate
    // and format it the same way this component formats dates ("Tue, Jul 28"),
    // falling back to the backend-provided detail string.
    getTransferDate(handle) {
      const lt = this._stockData && this._stockData.leadTimes && this._stockData.leadTimes[handle];
      if (!lt) return '';
      if (lt.readyDate) {
        const d = new Date(`${lt.readyDate}T00:00:00Z`);
        if (!Number.isNaN(d.getTime())) {
          return new Intl.DateTimeFormat('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC'
          }).format(d);
        }
      }
      return lt.detail || '';
    }

    // The ships-to-store ETA as a sentence fragment, so every surface degrades the
    // same way when the backend has no lead time for this item (on no shelf, not at
    // BCI, vendor has no direct-ship cutoff). Reads correctly after both "Get it …"
    // and "Pickup 2 later …". Without this the callers interpolated an empty string
    // and rendered the literal "Get it .".
    getPickupEtaPhrase(handle) {
      const date = this.getTransferDate(handle);
      return date ? `by ${date}` : 'in 3–5 business days';
    }

    formatDistance(distance) {
      if (!Number.isFinite(distance)) return '';
      return `${distance < 10 ? distance.toFixed(1) : Math.round(distance)} mi`;
    }

    formatHandle(handle) {
      return String(handle || '')
        .split('-')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    escapeHTML(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    renderPreview(sectionInnerHTML) {
      if (this.isFulfillmentPanel()) {
        this.renderFulfillmentPanel(this._stockData, sectionInnerHTML);
        return;
      }

      const selectedStore = localStorage.getItem('selected-store');

      if ( this.storeSelector && selectedStore && this.storeSelector.storesList[selectedStore] ) {
        // search based on selected store
        let storeFound = false;
        if ( selectedStore ) {
          sectionInnerHTML.querySelectorAll('.pickup-availability-alert').forEach(elm=>{
            if ( selectedStore == elm.dataset.store ) {
              this.innerHTML = elm.innerHTML.replace('{{ store }}', this.storeSelector.storesList[selectedStore]);
              storeFound = true;
            }
          })
        }
        if ( !storeFound ) {
          this.innerHTML = sectionInnerHTML.querySelector('.pickup-availability-alert[data-default-unavailable]').innerHTML.replace('{{ store }}', this.storeSelector.storesList[selectedStore]);
          storeFound = true;
        }
        if ( !selectedStore || !storeFound ) {
          this.innerHTML = '';
        }
      } else {
        // search based on default store
        this.innerHTML = sectionInnerHTML.querySelector('.pickup-availability-alert[data-default-store]').innerHTML;
      }

    }

  }

  if ( typeof customElements.get('pickup-availability-compact') == 'undefined' ) {
    customElements.define('pickup-availability-compact', PickupAvailabilityCompact);
	}

}

/* cache-bust 1778000000005 */
